'use client'

import { useState } from 'react'
import { clsx } from 'clsx'
import { Bookmark, BookmarkCheck } from 'lucide-react'
import { toggleBookmark } from '@/lib/supabase/bookmarks'
import type { BookmarkSubject } from '@/types'

const NOUN: Record<BookmarkSubject, string> = {
  thread: 'discussion',
  job: 'job',
  article: 'article',
  event: 'event',
  course: 'course',
  member: 'member',
}

/**
 * Save for later. Private — a bookmark produces no public signal and no
 * counter, so unlike a vote it stays available to a restricted member.
 *
 * Same interaction contract as VoteButton: optimistic, rolled back on failure,
 * guarded against a double-click, and it never fails silently.
 */
export function BookmarkButton({
  subjectType,
  subjectId,
  saved: initialSaved,
  viewerId,
  variant = 'icon',
  className,
  onChange,
}: {
  subjectType: BookmarkSubject
  subjectId: string
  saved: boolean
  viewerId?: string | null
  variant?: 'icon' | 'text'
  className?: string
  /** Fires after the server settles — lets /saved drop a row as it is unsaved. */
  onChange?: (saved: boolean) => void
}) {
  const [saved, setSaved] = useState(initialSaved)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [synced, setSynced] = useState(initialSaved)

  // Adopt later-arriving parent state without clobbering an in-flight toggle.
  // See the longer note in VoteButton — same pattern, same reason.
  if (!pending && synced !== initialSaved) {
    setSynced(initialSaved)
    setSaved(initialSaved)
  }

  if (!viewerId) return null

  const noun = NOUN[subjectType]

  async function handleClick(e: React.MouseEvent) {
    // Cards and rows are often wrapped in a <Link>.
    e.preventDefault()
    e.stopPropagation()
    if (pending) return

    const prev = saved
    setPending(true)
    setError(null)
    setSaved(!saved)

    const result = await toggleBookmark(subjectType, subjectId)
    setPending(false)

    if (!result.ok) {
      setSaved(prev)
      setError(result.error)
      return
    }
    setSaved(result.saved)
    onChange?.(result.saved)
  }

  const Icon = saved ? BookmarkCheck : Bookmark

  return (
    <span className={clsx('inline-flex items-center gap-1.5', className)}>
      <button
        type="button"
        onClick={handleClick}
        aria-pressed={saved}
        aria-label={saved ? `Remove this ${noun} from saved` : `Save this ${noun}`}
        title={saved ? 'Saved — click to remove' : 'Save for later'}
        className={clsx(
          'inline-flex items-center gap-1.5 rounded-lg transition-colors',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary',
          variant === 'icon' ? 'p-1.5' : 'px-2.5 py-1.5 text-[12px] font-semibold',
          pending && 'opacity-60',
          saved ? 'text-brand-text' : 'text-text-muted hover:text-text-primary'
        )}
      >
        <Icon className={clsx('w-4 h-4', saved && 'fill-current')} aria-hidden />
        {variant === 'text' && (saved ? 'Saved' : 'Save')}
      </button>
      {error && (
        <span role="status" className="text-[12px] text-danger">
          {error}
        </span>
      )}
    </span>
  )
}
